import { useCallback, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { X, Eraser, Check, UploadCloud, PenLine } from 'lucide-react'
import { useProjectStore } from '../store/projectStore.js'

export default function SignatureModal({ onClose }) {
  const addSignature = useProjectStore((s) => s.addSignature)
  const showToast = useProjectStore((s) => s.showToast)

  const [mode, setMode] = useState('draw') // 'draw' | 'upload'
  const [name, setName] = useState('Signature')
  const canvasRef = useRef(null)
  const drawing = useRef(false)
  const last = useRef(null)
  const [uploaded, setUploaded] = useState(null)

  const clear = () => {
    const c = canvasRef.current
    if (!c) return
    const ctx = c.getContext('2d')
    ctx.clearRect(0, 0, c.width, c.height)
  }

  const handlePointerDown = (e) => {
    const c = canvasRef.current
    if (!c) return
    c.setPointerCapture(e.pointerId)
    drawing.current = true
    const r = c.getBoundingClientRect()
    last.current = { x: e.clientX - r.left, y: e.clientY - r.top }
  }
  const handlePointerMove = (e) => {
    if (!drawing.current) return
    const c = canvasRef.current
    if (!c) return
    const ctx = c.getContext('2d')
    const r = c.getBoundingClientRect()
    const x = e.clientX - r.left
    const y = e.clientY - r.top
    ctx.strokeStyle = '#0f172a'
    ctx.lineWidth = 2.4
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.beginPath()
    ctx.moveTo(last.current.x, last.current.y)
    ctx.lineTo(x, y)
    ctx.stroke()
    last.current = { x, y }
  }
  const handlePointerUp = () => {
    drawing.current = false
    last.current = null
  }

  const onDrop = useCallback(async (files) => {
    const file = files?.[0]
    if (!file) return
    if (!/^image\/(png|jpe?g)$/i.test(file.type)) {
      showToast('Use a PNG or JPG image.', 'warning')
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      const img = new Image()
      img.onload = () =>
        setUploaded({ dataUrl: reader.result, width: img.width, height: img.height })
      img.src = reader.result
    }
    reader.readAsDataURL(file)
  }, [showToast])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'] },
    multiple: false,
  })

  const save = async () => {
    let dataUrl, width, height
    if (mode === 'draw') {
      const c = canvasRef.current
      if (!c) return
      // Trim transparent pixels to a tight bounding box for better placement.
      const ctx = c.getContext('2d')
      const img = ctx.getImageData(0, 0, c.width, c.height)
      const d = img.data
      let minX = c.width, minY = c.height, maxX = 0, maxY = 0
      let any = false
      for (let y = 0; y < c.height; y += 1) {
        for (let x = 0; x < c.width; x += 1) {
          const i = (y * c.width + x) * 4 + 3
          if (d[i] > 8) {
            any = true
            if (x < minX) minX = x
            if (y < minY) minY = y
            if (x > maxX) maxX = x
            if (y > maxY) maxY = y
          }
        }
      }
      if (!any) {
        showToast('Draw a signature first.', 'warning')
        return
      }
      const pad = 6
      minX = Math.max(0, minX - pad)
      minY = Math.max(0, minY - pad)
      maxX = Math.min(c.width - 1, maxX + pad)
      maxY = Math.min(c.height - 1, maxY + pad)
      const w = maxX - minX + 1
      const h = maxY - minY + 1
      const trimmed = document.createElement('canvas')
      trimmed.width = w
      trimmed.height = h
      trimmed.getContext('2d').drawImage(c, minX, minY, w, h, 0, 0, w, h)
      dataUrl = trimmed.toDataURL('image/png')
      width = w
      height = h
    } else {
      if (!uploaded) {
        showToast('Upload an image first.', 'warning')
        return
      }
      dataUrl = uploaded.dataUrl
      width = uploaded.width
      height = uploaded.height
    }
    addSignature({ name: name || 'Signature', dataUrl, width, height })
    showToast('Signature saved. Pick the Signature tool to place it.', 'success')
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-xl rounded-2xl bg-white p-5 shadow-lift dark:bg-slate-900 dark:text-slate-100">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold">Add signature</h2>
          <button onClick={onClose} className="rounded-md p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mb-3 inline-flex rounded-lg bg-slate-100 p-0.5 dark:bg-slate-800">
          <button
            onClick={() => setMode('draw')}
            className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium ${
              mode === 'draw' ? 'bg-white shadow-soft dark:bg-slate-700' : 'text-slate-500'
            }`}
          >
            <PenLine className="h-3.5 w-3.5" /> Draw
          </button>
          <button
            onClick={() => setMode('upload')}
            className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium ${
              mode === 'upload' ? 'bg-white shadow-soft dark:bg-slate-700' : 'text-slate-500'
            }`}
          >
            <UploadCloud className="h-3.5 w-3.5" /> Upload
          </button>
        </div>

        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Signature name"
          className="input mb-3 h-9 text-sm dark:bg-slate-800 dark:text-slate-100"
        />

        {mode === 'draw' && (
          <div className="rounded-lg border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-800">
            <canvas
              ref={canvasRef}
              width={560}
              height={200}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              className="block w-full cursor-crosshair touch-none rounded-md bg-slate-50 dark:bg-slate-900"
              style={{ aspectRatio: '560/200', touchAction: 'none' }}
            />
            <button
              onClick={clear}
              className="btn-ghost mt-2 h-8 px-2 text-xs"
            >
              <Eraser className="h-3.5 w-3.5" /> Clear
            </button>
          </div>
        )}

        {mode === 'upload' && (
          <div
            {...getRootProps()}
            className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 text-center ${
              isDragActive
                ? 'border-brand-500 bg-brand-50/40'
                : 'border-slate-300 dark:border-slate-600'
            }`}
          >
            <input {...getInputProps()} />
            {uploaded ? (
              <img
                src={uploaded.dataUrl}
                alt="Signature"
                className="max-h-32"
              />
            ) : (
              <>
                <UploadCloud className="h-7 w-7 text-slate-400" />
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Drop or click to upload a transparent PNG / JPG
                </p>
              </>
            )}
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary h-9 px-3 text-xs dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
            Cancel
          </button>
          <button onClick={save} className="btn-primary h-9 px-3 text-xs">
            <Check className="h-3.5 w-3.5" /> Save signature
          </button>
        </div>
      </div>
    </div>
  )
}
